import React, { useEffect, useRef, useState } from 'react';

const TalentGrid: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('ALL');

  const roles = [
    {
      id: "SA-01",
      category: "ARCHITECTURE",
      title: "Solutions Architect",
      value: "Structural Integrity",
      description: "Translating business requirements into scalable, future-proof systems designed for enterprise longevity.",
    },
    {
      id: "DE-04",
      category: "OPERATIONS",
      title: "DevOps Engineer",
      value: "System Reliability",
      description: "Automating mission-critical delivery pipelines and ensuring 99.9% uptime targets for global workloads.",
    },
    {
      id: "FD-09",
      category: "ENGINEERING",
      title: "Full-Stack Developer",
      value: "Technical Velocity",
      description: "Building high-performance applications with clean code standards and seamless enterprise integration.",
    },
    {
      id: "PM-12",
      category: "STRATEGY",
      title: "Program Manager",
      value: "Efficiency Ops",
      description: "Bridging the gap between stakeholders and technical teams to ensure on-time, on-budget delivery.",
    }
  ];

  const filteredRoles = activeFilter === 'ALL' 
    ? roles 
    : roles.filter(r => r.category === activeFilter);

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [activeFilter]);

  return (
    <section ref={sectionRef} className="py-16 md:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-end justify-between mb-16 md:mb-24">
          <div className="max-w-xl reveal">
            <div className="mono text-[10px] text-[#0091da] font-bold mb-4 tracking-[0.3em] uppercase">// Technical Roster</div>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-950 mb-6 tracking-tight">The Talent Grid</h2>
            <p className="text-lg md:text-xl text-slate-500 font-light leading-relaxed">
              Senior-level practitioners selected for technical depth, operational experience, and business acumen.
            </p>
          </div>
          <div className="reveal mt-8 md:mt-0 flex flex-wrap gap-2 md:gap-4" style={{ transitionDelay: '200ms' }}>
            {['ALL', 'ARCHITECTURE', 'ENGINEERING', 'OPERATIONS', 'STRATEGY'].map(f => (
              <button 
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`mono text-[8px] md:text-[9px] tracking-widest uppercase px-4 md:px-6 py-2 border rounded-full transition-all duration-300 ${activeFilter === f ? 'bg-slate-900 text-white border-slate-900 shadow-lg shadow-slate-900/10' : 'text-slate-400 border-slate-200 hover:border-slate-900 hover:text-slate-900'}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {filteredRoles.map((role, i) => (
            <div 
              key={`${role.id}-${activeFilter}`} 
              className="reveal bg-white p-8 md:p-12 group relative overflow-hidden transition-all hover:bg-slate-50"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Scanline element */}
              <div className="scanline"></div>
              
              <div className="mono text-[10px] text-slate-400 mb-8 flex justify-between relative z-20">
                <span>ID: {role.id}</span>
                <span className="text-[#0091da] font-bold uppercase tracking-widest">Available</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-950 mb-3 relative z-20 tracking-tight group-hover:text-[#0091da] transition-colors">{role.title}</h3>
              <p className="mono text-[10px] text-[#facc15] font-bold uppercase tracking-widest mb-8 relative z-20">{role.value}</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-10 min-h-[80px] font-light relative z-20">
                {role.description}
              </p>
              <div className="flex justify-end translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 relative z-20">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-[#0091da]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TalentGrid;